import React, { useState, Children } from 'react';
import { CTAButton } from '../components/CTAButton';
import { FAQAccordion } from '../components/FAQAccordion';
import { LegalModal } from '../components/LegalModal';
import { ShieldAlert, Scale, FileText, Clock, CheckCircle2, XCircle, ShieldCheck, BookOpen, FileSignature, Target } from 'lucide-react';
export function SalesPage() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const scrollToPricing = () => {
    document.getElementById('pricing-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-dark text-light selection:bg-gold selection:text-dark" data-id="element-21">
      {/* Navigation / Top Bar */}
      <header className="border-b border-gold/20 bg-dark/95 backdrop-blur-sm sticky top-0 z-50" data-id="element-22">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between" data-id="element-23">
          <div className="flex items-center gap-2" data-id="element-24">
            <Scale className="w-8 h-8 text-gold" data-id="element-25" />
            <span className="font-serif text-xl font-bold tracking-wider uppercase text-gold-light" data-id="element-26">
              Financial Authority
            </span>
          </div>
          <CTAButton onClick={scrollToPricing} className="py-2 px-6 text-sm hidden sm:flex" showIcon={false} data-id="element-27">
            Get Access
          </CTAButton>
        </div>
      </header>

      <main data-id="element-28">
        {/* Hero Section */}
        <section className="relative pt-20 pb-32 overflow-hidden border-b border-gold/20" data-id="element-29">
          {/* Subtle background glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold/5 rounded-full blur-[120px] pointer-events-none" data-id="element-30" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-31">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-32">
              <div className="max-w-2xl" data-id="element-33">
                <div className="inline-flex items-center gap-2 px-4 py-2 border border-gold/30 bg-gold/5 rounded-sm mb-8" data-id="element-34">
                  <ShieldCheck className="w-4 h-4 text-gold" data-id="element-35" />
                  <span className="text-sm font-semibold tracking-widest uppercase text-gold" data-id="element-36">
                    Legal Strategy Guide
                  </span>
                </div>

                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6" data-id="element-37">
                  THE 72-HOUR <br data-id="element-38" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-light via-gold to-gold-dark" data-id="element-39">
                    CREDIT RESET:
                  </span>
                  <br data-id="element-40" />
                  <span className="text-3xl sm:text-4xl lg:text-5xl text-light/90 font-normal italic" data-id="element-41">
                    The Consumer Law Loophole
                  </span>
                </h1>

                <p className="text-xl sm:text-2xl text-light/80 leading-relaxed mb-10 font-serif" data-id="element-42">
                  How to delete critical credit errors and flip a "Denied" to
                  "Approved" in one weekend without expensive lawyers.
                </p>

                <div className="flex flex-col sm:flex-row gap-4" data-id="element-43">
                  <CTAButton onClick={scrollToPricing} className="w-full sm:w-auto" data-id="element-44">
                    Get Instant Access Now
                  </CTAButton>
                </div>
                <p className="mt-4 text-sm text-light/50 flex items-center gap-2" data-id="element-45">
                  <ShieldCheck className="w-4 h-4" data-id="element-46" /> Secure SSL Encryption •
                  Instant PDF Download
                </p>
              </div>

              <div className="relative lg:ml-auto w-full max-w-lg mx-auto" data-id="element-47">
                <div className="absolute inset-0 bg-gradient-to-tr from-gold/20 to-transparent blur-2xl rounded-full" data-id="element-48" />
                <img src="https://cdn.magicpatterns.com/uploads/uiDsJjTCjMnZCAH9VWZkPX/heylloyd-image-1773282799868.png" alt="72-Hour Credit Reset Bundle on a laptop in an executive office" className="relative z-10 w-full h-auto rounded-sm shadow-2xl border border-gold/20" data-id="element-49" />
              </div>
            </div>
          </div>
        </section>

        {/* The Hook / The Problem */}
        <section className="py-24 bg-dark-lighter border-b border-gold/20" data-id="element-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-51">
            <div className="prose prose-lg prose-invert mx-auto" data-id="element-52">
              <p className="text-2xl leading-relaxed font-serif text-gold-light italic mb-8 border-l-4 border-gold pl-6" data-id="element-53">
                It's 5:00 PM on a Friday. You're at the dealership or the
                leasing office, ready to move forward with your life. Then comes
                the awkward silence. The manager looks down at a paper and says
                the words you dread: "I'm sorry, we couldn't get the approval.
                Your score is just too low."
              </p>

              <p className="text-xl text-light/90 mb-12" data-id="element-54">
                In that moment, your future is put on hold by a three-digit
                number that feels like a prison sentence. But what if that
                number is based on a lie?
              </p>

              <h2 className="text-4xl font-bold text-light mb-6" data-id="element-55">
                The Multi-Billion Dollar Lie
              </h2>
              <p className="text-lg text-light/80 mb-6" data-id="element-56">
                The credit bureaus—Equifax, Experian, and TransUnion—are not
                government agencies. They are multi-billion dollar corporations
                selling data about you. And that data is often wrong.
              </p>
              <p className="text-lg text-light/80 mb-12" data-id="element-57">
                They count on you being too tired, too busy, or too intimidated
                to fight back. They want you to believe that "time" is the only
                healer, while they continue to profit from your high interest
                rates and rejected applications.
              </p>
            </div>
          </div>
        </section>

        {/* Why What You Tried Didn't Work */}
        <section className="py-24 border-b border-gold/20" data-id="element-58">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-59">
            <div className="text-center mb-16" data-id="element-60">
              <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-61">
                Why The "Standard" Methods Fail
              </h2>
              <div className="w-24 h-1 bg-gold mx-auto" data-id="element-62" />
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-63">
              {[{
              title: 'Monthly Subscriptions',
              desc: 'Credit repair services charge you $99/month just to send slow, generic letters that bureaus easily ignore.'
            }, {
              title: 'Standard Dispute Apps',
              desc: "They only use 'electronic' disputes which are processed by AI and rejected in seconds."
            }, {
              title: 'Waiting 7 Years',
              desc: "A life sentence you don't have to serve if the information is inaccurate."
            }, {
              title: 'Generic Internet Templates',
              desc: "Flagged by bureau software as 'frivolous' before a human ever sees them."
            }].map((item, i) => <div key={i} className="bg-dark-lighter p-8 border border-red-900/30 rounded-sm flex gap-4 items-start" data-id="element-64">
                  <XCircle className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" data-id="element-65" />
                  <div data-id="element-66">
                    <h3 className="text-xl font-bold text-light mb-2 font-serif" data-id="element-67">
                      {item.title}
                    </h3>
                    <p className="text-light/70" data-id="element-68">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* The Unique Mechanism */}
        <section className="py-24 bg-dark-lighter border-b border-gold/20 relative overflow-hidden" data-id="element-69">
          <div className="absolute right-0 top-0 w-1/3 h-full bg-gradient-to-l from-gold/5 to-transparent pointer-events-none" data-id="element-70" />

          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-71">
            <div className="flex items-center gap-4 mb-8" data-id="element-72">
              <Scale className="w-12 h-12 text-gold" data-id="element-73" />
              <h2 className="text-4xl md:text-5xl font-bold text-gold-light" data-id="element-74">
                The Consumer Law Loophole
              </h2>
            </div>

            <div className="prose prose-lg prose-invert max-w-none" data-id="element-75">
              <p className="text-2xl text-light mb-8 font-serif" data-id="element-76">
                This isn't a "request" for help—it's a legal demand.
              </p>
              <p className="text-lg text-light/80 mb-6" data-id="element-77">
                By utilizing{' '}
                <strong data-id="element-78">
                  15 U.S. Code § 1681 (The Fair Credit Reporting Act)
                </strong>
                , we bypass the automated scanners and force a manual human
                review.
              </p>
              <div className="bg-dark p-8 border-l-4 border-gold my-8" data-id="element-79">
                <p className="text-xl text-gold-light m-0 font-serif italic" data-id="element-80">
                  "We don't argue about whether you 'owe' the money; we demand
                  the bureaus prove they followed every strict legal procedure
                  required to report it. If they can't produce the original
                  contract with your signature, the law says they MUST delete
                  it."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What You Get & How It Works (Two Columns) */}
        <section className="py-24 border-b border-gold/20" data-id="element-81">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-82">
            <div className="grid lg:grid-cols-2 gap-16" data-id="element-83">
              {/* What You Get */}
              <div data-id="element-84">
                <h2 className="text-3xl font-bold mb-8 flex items-center gap-3" data-id="element-85">
                  <BookOpen className="text-gold" data-id="element-86" /> Inside The Bundle
                </h2>
                <div className="space-y-6" data-id="element-87">
                  {[{
                  title: 'The Complete 72-Hour Credit Reset eBook',
                  desc: 'Your step-by-step master guide.'
                }, {
                  title: "The 'Disruptive Demand Letter' Template",
                  desc: 'The exact legal language that triggers deletions.'
                }, {
                  title: 'The 72-Hour Follow-Up Tracker',
                  desc: 'Manage bureau responses like a pro.'
                }, {
                  title: "The 'Pre-Dispute Shield' Checklist",
                  desc: 'Lock your report from new negatives.'
                }, {
                  title: "The CFPB 'Big Brother' Strike Protocol",
                  desc: 'For when bureaus try to stall.'
                }].map((item, i) => <div key={i} className="flex gap-4 items-start bg-dark-lighter p-6 border border-gold/10 rounded-sm" data-id="element-88">
                      <CheckCircle2 className="w-6 h-6 text-gold flex-shrink-0 mt-1" data-id="element-89" />
                      <div data-id="element-90">
                        <h4 className="font-bold text-lg text-light mb-1 font-serif" data-id="element-91">
                          {item.title}
                        </h4>
                        <p className="text-light/70 text-sm" data-id="element-92">{item.desc}</p>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* How It Works */}
              <div data-id="element-93">
                <h2 className="text-3xl font-bold mb-8 flex items-center gap-3" data-id="element-94">
                  <Target className="text-gold" data-id="element-95" /> The 3-Step Process
                </h2>
                <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-gold before:via-gold/50 before:to-transparent" data-id="element-96">
                  {[{
                  step: '01',
                  title: 'Identify',
                  desc: "Locate specific 'Data Linkage' errors using our Credit Rights Map."
                }, {
                  step: '02',
                  title: 'Demand',
                  desc: 'Send the Disruptive Demand Letter using our high-priority legal templates.'
                }, {
                  step: '03',
                  title: 'Enforce',
                  desc: 'Follow the 72-hour tracker to confirm delivery and trigger the CFPB if they stall.'
                }].map((item, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-97">
                      <div className="flex items-center justify-center w-12 h-12 rounded-full border-2 border-gold bg-dark text-gold font-bold z-10 shadow-[0_0_10px_rgba(201,168,76,0.5)]" data-id="element-98">
                        {item.step}
                      </div>
                      <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-6 bg-dark-lighter border border-gold/20 rounded-sm ml-4 md:ml-0" data-id="element-99">
                        <h4 className="font-bold text-xl text-gold-light mb-2 font-serif" data-id="element-100">
                          {item.title}
                        </h4>
                        <p className="text-light/70" data-id="element-101">{item.desc}</p>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Who This Is For / Not For */}
        <section className="py-24 bg-dark-lighter border-b border-gold/20" data-id="element-102">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-103">
            <div className="grid md:grid-cols-2 gap-8" data-id="element-104">
              {/* For */}
              <div className="bg-dark p-10 border-t-4 border-gold shadow-2xl" data-id="element-105">
                <h3 className="text-2xl font-bold mb-8 text-light flex items-center gap-3" data-id="element-106">
                  <CheckCircle2 className="text-gold w-8 h-8" data-id="element-107" /> Who This Is For
                </h3>
                <ul className="space-y-6" data-id="element-108">
                  {['Anyone recently denied for a car loan, mortgage, or apartment.', "People tired of paying $100+ a month to 'credit repair' companies with no results.", "Individuals with 'zombie debt' or errors they've already paid that won't disappear.", 'Motivation-driven pros who want to take control of their own financial identity.'].map((text, i) => <li key={i} className="flex gap-4 text-light/80" data-id="element-109">
                      <div className="w-2 h-2 rounded-full bg-gold mt-2.5 flex-shrink-0" data-id="element-110" />
                      <span data-id="element-111">{text}</span>
                    </li>)}
                </ul>
              </div>

              {/* Not For */}
              <div className="bg-dark p-10 border-t-4 border-red-900 shadow-2xl" data-id="element-112">
                <h3 className="text-2xl font-bold mb-8 text-light/70 flex items-center gap-3" data-id="element-113">
                  <XCircle className="text-red-900 w-8 h-8" data-id="element-114" /> Who This Is NOT
                  For
                </h3>
                <ul className="space-y-6" data-id="element-115">
                  {["People looking for a 'magic wand' to delete legitimate, verifiable debt they currently owe.", 'Those unwilling to spend 15 minutes at a post office sending a certified letter.', 'Anyone looking for legal representation (this is an educational guide).'].map((text, i) => <li key={i} className="flex gap-4 text-light/60" data-id="element-116">
                      <div className="w-2 h-2 rounded-full bg-red-900 mt-2.5 flex-shrink-0" data-id="element-117" />
                      <span data-id="element-118">{text}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Proof / Believability */}
        <section className="py-24 border-b border-gold/20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-119">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-120">
            <ShieldAlert className="w-16 h-16 text-gold mx-auto mb-8" data-id="element-121" />
            <h2 className="text-3xl md:text-4xl font-bold mb-8 font-serif text-gold-light" data-id="element-122">
              The Expected Result
            </h2>
            <p className="text-2xl text-light/90 leading-relaxed italic border-y border-gold/20 py-10" data-id="element-123">
              "Most users see a 'Deletion Notice' or a significant score jump
              within the first 14-30 days of the bureaus receiving the demand,
              often turning a 'Denied' into a 'Conditional Approval' in a
              fraction of the time of traditional repair."
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-dark-lighter border-b border-gold/20" data-id="element-124">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-125">
            <div className="text-center mb-16" data-id="element-126">
              <h2 className="text-4xl font-bold mb-4" data-id="element-127">
                Frequently Asked Questions
              </h2>
              <div className="w-16 h-1 bg-gold mx-auto" data-id="element-128" />
            </div>

            <div className="space-y-2" data-id="element-129">
              <FAQAccordion question="Is this legal?" answer="Yes, it is based entirely on the Fair Credit Reporting Act, a federal law designed to protect consumers from inaccurate reporting." data-id="element-130" />
              <FAQAccordion question="Do I need a lawyer?" answer="No. This guide gives you the exact language to represent yourself and demand compliance directly from the bureaus." data-id="element-131" />
              <FAQAccordion question="Will it work for bankruptcies?" answer="Yes, the guide covers the specific 'Third-Party Verification' strategy required for public records." data-id="element-132" />
              <FAQAccordion question="How much does it cost?" answer="Just a one-time payment for the guide—no monthly fees, no hidden subscriptions, ever." data-id="element-133" />
            </div>
          </div>
        </section>

        {/* Offer & Pricing (CTA Section) */}
        <section id="pricing-section" className="py-32 relative overflow-hidden" data-id="element-134">
          <div className="absolute inset-0 bg-gradient-to-b from-dark to-darker" data-id="element-135" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-[500px] bg-gold/10 blur-[150px] pointer-events-none" data-id="element-136" />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-137">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 font-serif" data-id="element-138">
              Reclaim Your Freedom
            </h2>
            <p className="text-2xl text-light/80 mb-12" data-id="element-139">
              Stop letting a flawed algorithm run your life. Get the "Approved"
              you deserve.
            </p>

            <div className="bg-dark/80 backdrop-blur-md border border-gold/30 p-12 rounded-sm shadow-[0_0_50px_rgba(201,168,76,0.1)] max-w-2xl mx-auto" data-id="element-140">
              <h3 className="text-3xl font-bold text-gold-light mb-2" data-id="element-141">
                The 72-Hour Credit Reset guide
              </h3>
              <p className="text-light/60 mb-8 uppercase tracking-widest text-sm" data-id="element-142">
                Complete Digital Access
              </p>

              <div className="flex justify-center items-baseline gap-2 mb-10" data-id="element-143">
                <span className="text-6xl font-bold text-light" data-id="element-144">$6.36</span>
                <span className="text-xl text-light/50 line-through" data-id="element-145">$197</span>
              </div>

              <CTAButton href="https://www.lanzadigital360.com/cart/48072664711393:1" className="w-full text-xl py-5 mb-6" data-id="element-146">
                Get Instant Access Now
              </CTAButton>

              <div className="flex flex-col items-center gap-3 text-sm text-light/50" data-id="element-147">
                <div className="flex items-center gap-2" data-id="element-148">
                  <ShieldCheck className="w-4 h-4 text-gold" data-id="element-149" /> One-time
                  payment. No subscriptions.
                </div>
                <div className="flex items-center gap-2" data-id="element-150">
                  <FileSignature className="w-4 h-4 text-gold" data-id="element-151" /> Secure SSL
                  Encryption.
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-darker py-12 border-t border-gold/10 text-center text-light/40 text-sm" data-id="element-152">
        <div className="max-w-7xl mx-auto px-4" data-id="element-153">
          <Scale className="w-8 h-8 text-gold/30 mx-auto mb-6" data-id="element-154" />
          <p className="mb-4" data-id="element-155">
            Disclaimer: This material is for educational purposes only and does
            not constitute legal advice. Results vary based on individual credit
            profiles.
          </p>
          <div className="flex items-center justify-center gap-4 mb-6 text-light/50" data-id="element-156">
            <button onClick={() => setShowPrivacy(true)} className="hover:text-gold transition-colors underline cursor-pointer" data-id="element-157">
              Privacy Policy
            </button>
            <span data-id="element-158">|</span>
            <button onClick={() => setShowTerms(true)} className="hover:text-gold transition-colors underline cursor-pointer" data-id="element-159">
              Terms of Service
            </button>
          </div>
          <p data-id="element-160">
            © {new Date().getFullYear()} Financial Authority. All rights
            reserved.
          </p>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy Policy" data-id="element-161">
        <p className="text-light/50 text-sm italic mb-6" data-id="element-162">
          Last updated: March 2026
        </p>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-163">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-164">
            Information We Collect
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-165">
            When you purchase our digital products, we collect essential
            information required to process your order and deliver the product.
            This includes your name, email address, and payment processing
            details. We do not collect any unnecessary personal data.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-166">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-167">
            How We Use Information
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-168">
            The information collected is used solely to deliver your purchased
            digital product, process your payment securely, and improve our
            services. We do not use your information for unrelated purposes.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-169">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-170">
            Third-Party Services
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-171">
            We use secure third-party payment processors to handle transactions.
            We do not sell, rent, or trade your personal data to any third
            parties under any circumstances.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-172">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-173">
            Data Security
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-174">
            We implement industry-standard security measures, including SSL
            encryption, to protect your personal information and ensure secure
            storage of any data we process.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-175">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-176">
            Cookies
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-177">
            Our website uses basic cookies strictly for analytics and to ensure
            the proper functioning of the site. These cookies do not track
            personal identifiable information across other websites.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-178">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-179">
            Your Rights
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-180">
            You have the right to request access to, correction of, or deletion
            of your personal data stored by Financial Authority.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-181">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-182">
            Children's Privacy
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-183">
            Our services and products are not directed at children under the age
            of 13. We do not knowingly collect personal information from
            children.
          </p>
        </div>

        <div className="pb-4 mb-4" data-id="element-184">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-185">
            Changes to Policy
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-186">
            Financial Authority reserves the right to update this Privacy Policy
            at any time. Any changes will be reflected on this page with an
            updated effective date.
          </p>
        </div>
      </LegalModal>

      {/* Terms of Service Modal */}
      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Terms of Service" data-id="element-187">
        <p className="text-light/50 text-sm italic mb-6" data-id="element-188">
          Last updated: March 2026
        </p>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-189">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-190">
            Acceptance of Terms
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-191">
            By accessing our website and purchasing our digital products, you
            agree to be bound by these Terms of Service. If you do not agree
            with any part of these terms, you may not use our services.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-192">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-193">
            Product Description
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-194">
            Financial Authority provides digital educational guides, including
            eBooks and templates. These materials are strictly for educational
            purposes and do not constitute legal or financial advice.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-195">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-196">
            Payment and Pricing
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-197">
            All products require a one-time payment. Prices are subject to
            change without notice. All sales are final upon completion of the
            transaction.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-198">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-199">
            Digital Delivery
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-200">
            Upon successful payment, you will receive instant access to download
            your purchased digital products. Ensure you save the files to your
            personal device.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-201">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-202">
            Intellectual Property
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-203">
            All content, materials, and templates provided are the exclusive
            intellectual property of Financial Authority. Your purchase grants
            you a single, personal-use license.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-204">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-205">
            No Redistribution
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-206">
            You may not share, resell, distribute, or reproduce any part of the
            digital products or materials provided by Financial Authority under
            any circumstances.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-207">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-208">
            Disclaimer
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-209">
            The information provided in our guides is for educational purposes
            only. We are not attorneys or financial advisors. Results vary based
            on individual circumstances, and we make no guarantees regarding
            specific outcomes.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-210">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-211">
            Limitation of Liability
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-212">
            Financial Authority shall not be held liable for any direct,
            indirect, incidental, or consequential damages resulting from the
            use or inability to use our educational materials.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-213">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-214">
            Refund Policy
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-215">
            Due to the immediate, digital nature of our products, all sales are
            final. We do not offer refunds once the digital product has been
            delivered or downloaded.
          </p>
        </div>

        <div className="border-b border-gold/10 pb-4 mb-4" data-id="element-216">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-217">
            Governing Law
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-218">
            These Terms of Service shall be governed by and construed in
            accordance with applicable US federal and state laws.
          </p>
        </div>

        <div className="pb-4 mb-4" data-id="element-219">
          <h3 className="text-gold-light font-bold text-lg mb-3 font-serif" data-id="element-220">
            Changes to Terms
          </h3>
          <p className="text-light/70 text-sm leading-relaxed mb-4" data-id="element-221">
            Financial Authority reserves the right to modify these Terms of
            Service at any time. Continued use of our products or website
            constitutes acceptance of the updated terms.
          </p>
        </div>
      </LegalModal>
    </div>;
}